import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useProblem } from '../hooks/useProblem';
import { api } from '../services/api';
import { AngleGallery } from '../components/AngleGallery';
import AISuggestionCard from '../components/AISuggestionCard';
import CreateAngleModal from '../components/CreateAngleModal';
import { AngleChatInterface } from '../components/AngleChatInterface';
import { ProblemInterviewModal } from '../components/ProblemInterviewModal';
import { DevToolsPanel } from '../components/DevToolsPanel';
import type { AISuggestion, AngleWithProgress, SaveContextRequest, Angle } from '@attack100/shared';

function ProblemDetail() {
  const { id } = useParams<{ id: string }>();
  const problemId = parseInt(id!);
  const { problem, loading, error, refetch } = useProblem(problemId);
  const [suggestion, setSuggestion] = useState<AISuggestion | null>(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeChat, setActiveChat] = useState<AngleWithProgress | null>(null);
  const [generatingAngles, setGeneratingAngles] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 100 });
  const [showInterviewModal, setShowInterviewModal] = useState(false);

  useEffect(() => {
    if (problem) {
      fetchSuggestion();
    }
  }, [problem]);

  const fetchSuggestion = async () => {
    try {
      setSuggestionLoading(true);
      const data = await api.getSuggestion(problemId);
      setSuggestion(data.suggestion);
    } catch (err) {
      console.error('Failed to fetch suggestion:', err);
    } finally {
      setSuggestionLoading(false);
    }
  };

  const handleActivateAngle = async (angle: Angle) => {
    try {
      await api.activateAngle(angle.id);
      await refetch();
      // Automatically open workspace after activation
      const refreshedProblem = await api.getProblem(problemId);
      const activatedAngle = refreshedProblem.problem.angles.find(a => a.id === angle.id);
      if (activatedAngle) {
        setActiveChat(activatedAngle);
      }
    } catch (err) {
      console.error('Failed to activate angle:', err);
      alert('Failed to activate angle. Please try again.');
    }
  };

  const handleViewDetails = async (angle: Angle) => {
    // Activate angle if not already active
    if (angle.status !== 'active') {
      await handleActivateAngle(angle);
    } else {
      setActiveChat(angle as AngleWithProgress);
    }
  };

  const handleChatClose = async () => {
    setActiveChat(null);
    await refetch();
    await fetchSuggestion();
  };

  const handleDeleteAngle = async (angle: Angle) => {
    try {
      await api.deleteAngle(angle.id);
      await refetch();
      await fetchSuggestion();
    } catch (err) {
      console.error('Failed to delete angle:', err);
      alert('Failed to delete angle. Please try again.');
    }
  };

  const handleAngleCreated = () => {
    setShowCreateModal(false);
    refetch();
  };

  const handleStartInterview = () => {
    setShowInterviewModal(true);
  };

  const handleInterviewComplete = async (context: SaveContextRequest) => {
    try {
      setShowInterviewModal(false);
      setGeneratingAngles(true);
      setGenerationProgress({ current: 0, total: 100 });

      // Save context first
      await api.saveContext(problemId, context);

      // Generate angles progressively (10 personas × 10 variations = 100)
      const personas = ['architect', 'economist', 'psychologist', 'storyteller', 'operator', 'strategist', 'builder', 'connector', 'scientist', 'red-team'];
      let angleCount = 0;

      for (const personaId of personas) {
        for (let variation = 1; variation <= 10; variation++) {
          try {
            await api.generateAngle(problemId, personaId, variation);
            angleCount++;
            setGenerationProgress({ current: angleCount, total: 100 });

            // Refresh problem data after each angle so they appear as they're generated
            await refetch();
          } catch (err) {
            console.error(`Failed to generate angle for ${personaId} variation ${variation}:`, err);
            // Continue with next angle even if one fails
          }
        }
      }

      // Final refresh to ensure all angles are shown
      await refetch();
      await fetchSuggestion();
    } catch (err: any) {
      console.error('Failed to generate angles:', err);
      alert(err.message || 'Failed to generate angles. Please try again.');
    } finally {
      setGeneratingAngles(false);
      setGenerationProgress({ current: 0, total: 100 });
    }
  };

  const handleInterviewSkip = async () => {
    try {
      setShowInterviewModal(false);
      setGeneratingAngles(true);
      setGenerationProgress({ current: 0, total: 100 });

      // Generate angles progressively (10 personas × 10 variations = 100)
      const personas = ['architect', 'economist', 'psychologist', 'storyteller', 'operator', 'strategist', 'builder', 'connector', 'scientist', 'red-team'];
      let angleCount = 0;

      for (const personaId of personas) {
        for (let variation = 1; variation <= 10; variation++) {
          try {
            await api.generateAngle(problemId, personaId, variation);
            angleCount++;
            setGenerationProgress({ current: angleCount, total: 100 });

            // Refresh problem data after each angle so they appear as they're generated
            await refetch();
          } catch (err) {
            console.error(`Failed to generate angle for ${personaId} variation ${variation}:`, err);
            // Continue with next angle even if one fails
          }
        }
      }

      // Final refresh to ensure all angles are shown
      await refetch();
      await fetchSuggestion();
    } catch (err: any) {
      console.error('Failed to generate angles:', err);
      alert(err.message || 'Failed to generate angles. Please try again.');
    } finally {
      setGeneratingAngles(false);
      setGenerationProgress({ current: 0, total: 100 });
    }
  };

  if (loading) {
    return (
      <div>
        <header className="header">
          <div className="header-content">
            <Link to="/" className="logo">Attack100</Link>
          </div>
        </header>
        <div className="loading">Loading problem...</div>
      </div>
    );
  }

  if (error || !problem) {
    return (
      <div>
        <header className="header">
          <div className="header-content">
            <Link to="/" className="logo">Attack100</Link>
          </div>
        </header>
        <div className="container">
          <div className="error">{error || 'Problem not found'}</div>
          <Link to="/" className="btn btn-primary">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <Link to="/" className="logo">Attack100</Link>
          <Link to="/" className="btn btn-secondary">
            Back to Dashboard
          </Link>
        </div>
      </header>

      <div className="container">
        {/* Problem Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
            {problem.title}
          </h1>
          <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
            {problem.description}
          </p>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div className="badge badge-primary">
              {problem.total_angles} angles
            </div>
            <div className="badge badge-success">
              {problem.average_progress.toFixed(1)}% average progress
            </div>
          </div>
        </div>

        {/* AI Suggestion */}
        {suggestion && (
          <AISuggestionCard
            suggestion={suggestion}
            loading={suggestionLoading}
            onRefresh={fetchSuggestion}
          />
        )}

        {/* Actions */}
        <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem' }}>
          {problem.total_angles === 0 ? (
            <>
              <button
                className="btn btn-primary"
                onClick={handleStartInterview}
                disabled={generatingAngles}
                style={{ fontSize: '1rem', padding: '0.75rem 1.5rem' }}
              >
                {generatingAngles ? `✨ Generating Angle ${generationProgress.current}/${generationProgress.total}...` : '✨ Generate 100 Angles with AI'}
              </button>
              {generatingAngles && (
                <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem', alignSelf: 'center' }}>
                  {Math.round((generationProgress.current / generationProgress.total) * 100)}% complete - Angles will appear as they're generated
                </span>
              )}
            </>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              + Add New Angle
            </button>
          )}
        </div>

        {/* Angle Gallery */}
        <AngleGallery
          angles={problem.angles}
          onActivate={handleActivateAngle}
          onViewDetails={handleViewDetails}
          onDelete={handleDeleteAngle}
        />

        {/* Create Angle Modal */}
        {showCreateModal && (
          <CreateAngleModal
            problemId={problemId}
            existingAngles={problem.angles}
            onClose={() => setShowCreateModal(false)}
            onCreated={handleAngleCreated}
          />
        )}

        {/* Interview Modal */}
        {showInterviewModal && (
          <ProblemInterviewModal
            problemId={problemId}
            onComplete={handleInterviewComplete}
            onSkip={handleInterviewSkip}
          />
        )}

        {/* Chat Interface - Only for active angles */}
        {activeChat && activeChat.status === 'active' && (
          <AngleChatInterface
            angleId={activeChat.id}
            angleTitle={activeChat.title}
            angleDescription={activeChat.description}
            onClose={handleChatClose}
          />
        )}

        {/* Dev Tools */}
        <DevToolsPanel problemId={problemId} />
      </div>
    </div>
  );
}

export default ProblemDetail;
